import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { bedienerPflicht } from "@mrmachine/fach";
import { einheitFuer, useT, type Uebersetzer } from "../i18n";
import { Erklaerung, Knopf, Marke, Punkt, alsZahl, betragstext, euro } from "./index";
import { Deckblatt } from "./Deckblatt";
import { Blattvonunten } from "./Blattvonunten";
import { Abholung } from "./Abholung";
import { abholungAusPreis, abholungMoeglich, abholungSteht } from "../daten/abholung";
import { Gespann } from "./Gespann";
import { Abschnitt, Kurzfassung, Leersatz, Wertzeile, Zeilenblatt } from "./Handyformen";
import { Blattsatz, Hakenzeile, Stueckzeile, Wahlzeile, Zahlzeile } from "./Postendetails";
import { ARTWORT } from "./Positionen";
import { Mengenzeile, mitMengenfeld } from "./Leistungsmenge";
import { useTelefon } from "../schale/geraet";
import type { Bedienerkandidat, Maschinentyp, Preisauskunft } from "../daten/typen";
import { AusstattungWahl, ausstattungVon, mitAusstattung } from "./Ausstattung";
import { Zeitraum, type Spanne } from "./Zeitraum";
import { ZUBEHOER_LEER, ZubehoerWahl, type ZubehoerWert } from "./ZubehoerWahl";
import { Anbauteile } from "./Anbauteile";
import { ReservierteStuecke, useStueckeImFenster } from "./Reserviert";
import { Postenaufstellung } from "./Postenaufstellung";

/** Was der Mensch an einem Zuschlag geändert hat. */
type Zuschlagsstand = { aus?: boolean | undefined; einzelpreis?: number | undefined };
import { zeitraumAbBis } from "./preistext";
import {
  useMaschinentypen, useMaschinenVorschlag, usePlanung, usePreis,
} from "../daten/haken";
import {
  fehlendePreise, gerechneterSatz, istMiete, mietsatz, postenAendern, postenAendernWennFrei, postenSumme, zuschlagNetto,
  type Merkposten, type Merkzuschlag,
} from "../daten/merkliste";
import { betragFehler, betragVerlassen, zahlLesen } from "../daten/zahlfeld";
import { tagKurz } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Ein Blatt je Posten — alles, was die Schnellerfassung an einer Zeile
   kann.

   docs/38-vermietmodul.md, Schritt 2: *„**Was die Schnellerfassung heute
   mehr kann, zieht in die Zeile um:** Ausstattung, Bediener, Zubehör,
   Zuschläge, Preis von Hand, „das letzte Mal war ja gut". Jeder Posten
   bekommt ein Blatt, in dem das steht — beim Anruf tippt man nichts
   davon, beim Sonderfall schon."*

   **Ein Blatt und keine tiefere Zeile.** Der Entwurf hat die Frage selbst
   gestellt: *„Ein Posten mit Ausstattung, Bediener, drei Zuschlägen und
   eigenem Preis ist beinahe eine Positionsmaske."* Genau deshalb ein
   Blatt: In der Sammlung steht, was gilt; wer es ändern will, macht es
   auf. Eine Zeile, die alles trägt, trägt beim Regelfall acht Felder
   spazieren, die niemand anfasst (Regel 11).

   **Gerechnet wird bei jeder Änderung neu** (Regel 4). Ausstattung,
   Zeitraum und Bediener ändern den Preis; der Posten trägt danach den
   frischen. Was von Hand daran steht — ein abgewählter Zuschlag, ein
   zugesagter Satz —, bleibt dabei stehen: Verglichen wird über den Code.

   **Und der Fahrer ist kein Beiwerk.** Luca, 17.09.2026: *„Ich habe den
   Kran genommen, aber ich wurde beim direkten Vermieten gar nicht dazu
   aufgefordert, den Fahrer auszuwählen. … Ich kann ja nicht den Auftrag
   verplanen, ohne zu sagen: Das machen wir so. Ich muss es dann manuell
   nachholen."* Was das Gerät verlangt (`nur_mit_person`), steht hier
   nicht zur Wahl, und die Person ist Pflicht — vorbelegt mit dem
   Stammfahrer, wenn es einen gibt und er frei ist (Migration 0070).

   ── Am Telefon: dasselbe Blatt wie im Angebot (20.09.2026) ─────────

   Luca: *„Was ich immer noch nicht kann, ist, die Einsätze in der Position
   zu bearbeiten, so wie wir es eigentlich fordern. … so wie beim
   Direktvermieten, sodass man da dann halt entsprechend auch die Position
   bearbeiten kann."* Und: *„Vor allem die Details sind auch noch nicht
   gut formatiert."*

   Bis dahin legte die Telefonfassung (`Handyfassung`) die Teile des
   Schreibtischblatts in den Leib — mit Fragezeichen, gestreckten Feldern
   und einer gelben Umschaltfläche für „ohne Bediener". Jetzt zeichnet sie
   die Formen des Blatts (`bausteine/Postendetails.tsx`, „Das Blatt einer
   Position am Telefon"): ein Abschnitt je Sache, Zeilen von 56 px, die
   Zahl rechts in Mono. Das Angebot zeichnet dieselben
   (`bausteine/Positionen.tsx`, `Positionsblatt`). **Die Rechnung ist
   dieselbe geblieben** — `usePreis`, `postenSumme`, `mietsatz` —, nur die
   Zeichnung ist neu.

   Klassen: `pb-*` (Schreibtisch), `posb-*` (Telefon), gegen alle Stile
   geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Wie viele Stück eine Zeile höchstens trägt. Darüber ist es ein Angebot. */
const MENGE_HOECHSTENS = 20;

/** Die zweite Zeile im Kopf des Blatts: die Art, und bei einer Miete die Gruppe. */
function blattUnter(t: Uebersetzer, p: Merkposten): string {
  return [t(ARTWORT[p.art] ?? "angebotBauen.artMiete"), istMiete(p) ? p.gruppe : ""]
    .filter(Boolean)
    .join(` ${t("allgemein.trennpunkt")} `);
}

export function Postenblatt({ offen, posten, kunde, schliessen }: {
  offen: boolean;
  /** `null`, solange keins offen ist — dann fragt das Blatt nichts nach. */
  posten: Merkposten | null;
  /** Die Kundennummer, damit die Kondition mitrechnet. Leer: Listenpreis. */
  kunde: string;
  schliessen: () => void;
}) {
  const t = useT();
  const telefon = useTelefon();
  const titel = posten?.bezeichnung || posten?.typ || t("merkliste.ohneNamen");

  /* **Am Telefon ein Blatt von unten** (19.09.2026). Luca: *„… sodass man
     da auch innerhalb eines Bottom Sheets das Detaillierte machen kann."*
     Seit dem 20.09.2026 öffnet die Zeile der Sammlung es direkt — kein
     Zwischenschritt „Details" mehr —, und **jede** Zeile mit Betrag: Eine
     Leistung oder Ware bekommt dasselbe Blatt mit Menge und Satz
     (`Weitereinhalt`). Die Summe des Postens steht im Fuß, wo der Daumen ist. */
  if (telefon) {
    const miete = posten ? istMiete(posten) : true;
    return (
      <Blattvonunten
        offen={offen && posten !== null}
        schliessen={schliessen}
        titel={titel}
        unter={posten ? blattUnter(t, posten) : undefined}
        rast="gross"
        fuss={posten ? (
          <>
            <div className="posb-fusssumme">
              <Wertzeile was={t("vermieten.blattSummeWas")} summe>
                {posten.gerechnet ? euro(postenSumme(posten)) : t("merkliste.preisOffen")}
              </Wertzeile>
            </div>
            <button type="button" className="handy-knopf" onClick={schliessen}>
              {t("vermieten.blattFertig")}
            </button>
          </>
        ) : undefined}
      >
        {posten
          ? (miete
            ? <Inhalt posten={posten} kunde={kunde} schliessen={schliessen} handy />
            : <Weitereinhalt posten={posten} />)
          : null}
      </Blattvonunten>
    );
  }

  return (
    <Deckblatt
      offen={offen && posten !== null}
      kompakt
      weite={760}
      schliessen={schliessen}
      titel={titel}
      untertitel={t("vermieten.blattUnter")}
    >
      {posten ? <Inhalt posten={posten} kunde={kunde} schliessen={schliessen} /> : null}
    </Deckblatt>
  );
}

/**
 * Das Feld für den Satz von Hand — sein Text und die Regel beim Verlassen.
 *
 * **Geleert und verlassen heißt „wieder der gerechnete", nicht „null".** Und
 * ein Betrag, der zufällig derselbe ist, ist kein Preis von Hand — sonst
 * friert die Annahme einen Preis ein, den niemand gesetzt hat. Dieselbe Regel
 * wie an der Karte der Sammlung (`Merkliste.tsx`).
 */
function useSatzfeld(p: Merkposten, gerechnet: number) {
  const [feld, setzeFeld] = useState("");
  useEffect(() => {
    setzeFeld(p.einzelpreis === undefined ? "" : betragstext(p.einzelpreis));
  }, [p.id, p.einzelpreis]);
  const schreiben = (text: string) => {
    // Eine dritte Stelle oder keine Zahl schreibt nichts (N4-06) — der Satz steht am Feld.
    if (betragFehler(text) !== null) return;
    const zahl = alsZahl(text);
    if (zahl === undefined || zahl === gerechnet) {
      setzeFeld("");
      postenAendern(p.id, { einzelpreis: undefined });
      return;
    }
    setzeFeld(betragstext(zahl));
    postenAendern(p.id, { einzelpreis: zahl });
  };
  return { feld, setzeFeld, schreiben };
}

/* ══════════════════════════════════════════════════════════════════
   Eine Leistung, eine Ware, ein Zuschlag im Blatt (20.09.2026).

   Sie haben weder Zeitraum noch Gerät — was sie haben, ist Menge und
   Satz. Bis hierher standen beide nur aufgeklappt an der Karte, mit dem
   Mengenschritt der Miete; seit die Zeile am Telefon das Blatt öffnet,
   steht es hier in derselben Form wie die Miete daneben.
   ══════════════════════════════════════════════════════════════════ */
function Weitereinhalt({ posten: p }: { posten: Merkposten }) {
  const t = useT();
  const satzGerechnet = gerechneterSatz(p);
  const { feld, setzeFeld, schreiben } = useSatzfeld(p, satzGerechnet);
  const [mengeFeld, setzeMengeFeld] = useState("");
  useEffect(() => { setzeMengeFeld(String(p.menge).replace(".", ",")); }, [p.id, p.menge]);
  const einheit = p.einheitKey ? t(p.einheitKey) : "";

  return (
    <div className="posb">
      <Abschnitt titel={t("positionen.blattRechnung")}>
        <Zeilenblatt>
          {/* **Eine Leistung mit Mengeneinheit** (08.10.2026): dieselbe Menge wie
              an der Karte am Schreibtisch — leer heißt „folgt aus dem Aufmaß",
              die Staffel rechnet mit, und der Vorschlag vom letzten Mal steht
              darunter (`Leistungsmenge.tsx`). */}
          {mitMengenfeld(p) ? <Mengenzeile p={p} /> : (
            <Zahlzeile
              was={t("angebotBauen.spalteMenge")}
              wert={mengeFeld}
              einheit={einheit || undefined}
              aendern={setzeMengeFeld}
              fertig={(text) => {
                const zahl = alsZahl(text);
                if (zahl === undefined || zahl <= 0) { setzeMengeFeld(String(p.menge).replace(".", ",")); return; }
                setzeMengeFeld(String(zahl).replace(".", ","));
                if (zahl !== p.menge) postenAendern(p.id, { menge: zahl });
              }}
            />
          )}
          <Zahlzeile
            was={t("positionen.blattSatz")}
            betrag
            schild={t("merkliste.satzSchild")}
            wert={feld}
            platz={betragstext(satzGerechnet)}
            einheit={einheit ? t("positionen.euroJe", { einheit }) : t("rabatt.einheitBetrag")}
            dazu={p.einzelpreis === undefined ? undefined : t("schnell.zeileVonHand", { betrag: euro(satzGerechnet) })}
            aendern={setzeFeld}
            fertig={schreiben}
          />
          <Wertzeile was={t("angebotBauen.spalteSumme")} betrag>
            {p.gerechnet ? euro(postenSumme(p)) : t("merkliste.preisOffen")}
          </Wertzeile>
        </Zeilenblatt>
      </Abschnitt>
    </div>
  );
}

function Inhalt({ posten, kunde, schliessen, handy = false }: {
  posten: Merkposten;
  kunde: string;
  schliessen: () => void;
  /** Die Telefonfassung: dieselbe Rechnung, die Formen des Blatts. */
  handy?: boolean;
}) {
  const t = useT();
  const p = posten;

  /* ── Der Stamm, für Ausstattung und Namen ───────────────────────── */
  const { data: typenStamm } = useMaschinentypen();
  const typ = useMemo(
    () => (typenStamm?.zeilen ?? []).find((x) => x.id === p.typId),
    [typenStamm, p.typId],
  );

  /* ── Der Preis, frisch ───────────────────────────────────────────
     Mit dem Kunden, mit der gewählten Ausstattung, mit der Antwort auf
     die Bedienerfrage — alle drei ändern ihn (Migration 0096). */
  const auskunft = usePreis(
    p.typId, p.von, p.bis, kunde || null, p.bedient, p.varianteId || undefined,
    { halten: true },
  );
  const preis = auskunft.data;
  const frisch = !auskunft.isPlaceholderData && preis !== undefined;

  /** Die Zuschläge, die ohne Nachfrage mitlaufen — die Liste, die gilt. */
  const vorgewaehlt = useMemo(
    () => (preis?.zuschlaege ?? []).filter((z) => z.vorgewaehlt),
    [preis],
  );

  /* **Der Posten übernimmt, was gerechnet wurde.** Sonst stünde in der
     Sammlung der Betrag von vorhin und im Blatt der von jetzt — zwei
     Zahlen für dieselbe Sache, und die falsche ist die, die niemand
     geholt hat. Was von Hand daran steht, wird dabei über den Code
     hinübergerettet: Wer einen Zuschlag abgewählt hat und danach die
     Ausstattung wechselt, hat ihn nicht wieder angewählt. */
  useEffect(() => {
    if (!preis || !frisch) return;
    const neu: Merkzuschlag[] = vorgewaehlt.map((z) => {
      const alt = p.zuschlaege.find((x) => x.code === z.code);
      return {
        code: z.code,
        bezeichnung: z.bezeichnung,
        netto: z.netto,
        menge: z.menge,
        /* `einzelpreis` steht hier bewusst NICHT, obwohl die Preisantwort einen
           mitbringt: Das Feld heißt „der **zugesagte** Satz", und wo es steht,
           rechnet `zuschlagNetto` Satz × Menge und lässt das gerechnete Netto
           liegen. Bei einer Regel mit Sockel ist das der falsche Betrag — die
           Anlieferung (Satz 0,00, Mindestbetrag 15,00) ginge mit 0,00 hinaus.
           Gesetzt wird es nur unten, aus `alt`, wo ein Mensch es zugesagt hat.
           `alsMerkzuschlag` in `daten/merkliste.ts` hält es genauso. */
        /* Die Marke „nicht abgestimmt" gehört zum Satz, nicht zur Anzeige:
           Fällt sie beim Neubauen der Zeile heraus, zeigt die Sammlung sie
           und das Blatt daneben nicht — dieselbe Zahl, einmal gekennzeichnet
           und einmal nicht. Das ist genau der Fall, vor dem die Marke warnt. */
        ...(z.platzhalter ? { platzhalter: true as const } : {}),
        /* **Was der Stamm über die Abholung sagt, geht mit** (19.09.2026).
           Bis hierher fiel `faelltBeiAbholung` beim Neubauen der Zeile weg —
           wer das Blatt einmal aufgemacht hatte, dem nahm „Kunde holt ab"
           die Anlieferung danach nicht mehr heraus. Und ein Zuschlag, den
           der Schalter abgewählt hat, muss das weiter wissen, damit er beim
           Zurückschalten wiederkommt (`daten/abholung.ts`). */
        ...(z.faelltBeiAbholung ? { faelltBeiAbholung: true as const } : {}),
        // Preis immer individuell (0227, E1) — geht mit, wie die Marke oben.
        ...(z.preisIndividuell ? { preisIndividuell: true as const } : {}),
        ...(z.jeAuftrag ? { jeAuftrag: true as const } : {}),
        ...(alt?.ausWegenAbholung ? { ausWegenAbholung: true as const } : {}),
        ...(alt?.aus ? { aus: true as const } : {}),
        ...(alt?.einzelpreis === undefined ? {} : { einzelpreis: alt.einzelpreis }),
      };
    });
    /* **Und was der Stamm über Abholung und Gespann sagt** (Systemprüfung
       A-02): Ein Posten, der ohne Preis gemerkt wurde, bekam es sonst nie,
       und der Schalter „Kunde holt ab" blieb weg. */
    const abholung = abholungAusPreis(preis);
    const gleich =
      p.gerechnet
      && p.netto === preis.netto
      && p.miettage === preis.miettage
      && p.einheiten === preis.menge
      && p.einheitKey === preis.einheit_key
      && p.bedienungspflicht === (preis.bedienungspflicht ?? "wahlweise")
      && (p.zuschlagsfrei === true) === (preis.zuschlagsfrei === true)
      && JSON.stringify(p.zuschlaege) === JSON.stringify(neu)
      && abholungSteht(p, abholung)
      && !p.zeitraumNeu;
    if (gleich) return;
    postenAendern(p.id, {
      gerechnet: true,
      netto: preis.netto,
      miettage: preis.miettage,
      einheiten: preis.menge,
      einheitKey: preis.einheit_key,
      bedienungspflicht: preis.bedienungspflicht ?? "wahlweise",
      zuschlaege: neu,
      zuschlaegeNetto: neu.reduce((s, z) => s + (z.aus ? 0 : z.netto), 0),
      /* Auf eine Pauschale ohne Zuschlaege schlaegt nichts auf (0144) — die
         Karte in der Sammlung sagt es, statt nichts zu zeigen. */
      zuschlagsfrei: preis.zuschlagsfrei === true,
      ...abholung,
      zeitraumNeu: undefined,
      ...(preis.zeitraum?.bis && preis.zeitraum.bis !== p.bis ? { bis: preis.zeitraum.bis } : {}),
    });
  }, [preis, frisch, vorgewaehlt, p]);

  /* ── Bestand und Menschen im Zeitraum ────────────────────────────
     Dieselbe Abfrage, mit der die Schnellerfassung ihre Trefferliste
     zeichnet — sie trägt Ressourcen und Balken in einem. */
  const { data: plan } = usePlanung(p.von, p.bis);
  const ressourcen = useMemo(() => plan?.ressourcen ?? [], [plan]);
  const belegungen = useMemo(() => plan?.belegungen ?? [], [plan]);

  /* ── Frei, reserviert, belegt ─────────────────────────────────────
     Seit N3 (24.09.2026) an einer Stelle für Blatt und Abschluss
     (`bausteine/Reserviert.tsx`) — dort steht auch, warum. */
  const { freieStuecke, reservierteStuecke } = useStueckeImFenster(p);

  /* **Vorbelegen, nicht festlegen** (Regel 11). Steht die Wahl schon auf
     einem Stück, das weiterhin frei ist, bleibt sie — sonst rutschte sie
     bei jedem Datenabgleich auf das erste zurück. */
  useEffect(() => {
    if (p.maschine && freieStuecke.some((r) => r.id === p.maschine)) return;
    // Eine bestätigte Reservierung ist eine Wahl, keine Vorbelegung — sie
    // bleibt stehen, bis jemand sie zurücknimmt.
    if (p.reservierungUebergehen && p.maschine
        && reservierteStuecke.some((x) => x.stueck.id === p.maschine)) return;
    const erste = freieStuecke[0]?.id ?? "";
    if (erste !== (p.maschine ?? "")) {
      postenAendern(p.id, { maschine: erste, ...(p.reservierungUebergehen ? { reservierungUebergehen: false } : {}) });
    }
  }, [freieStuecke, reservierteStuecke, p.maschine, p.reservierungUebergehen, p.id]);

  const stueck = useMemo(
    () => ressourcen.find((r) => r.id === p.maschine),
    [ressourcen, p.maschine],
  );

  const { data: vorschlag } = useMaschinenVorschlag(stueck?.res_id, p.von, p.bis);

  /* ── Wer fahren kann — und wer nicht, mit Grund ───────────────────
     Ein Mensch ist eine Ressource (Regel 8): frei heißt, im Fenster keine
     feste Belegung. **Und die Qualifikation, die das Gerät verlangt**
     (Systemprüfung A-06/E-02, 24.09.2026): Bis hierher stand hier jeder als
     frei, der nichts vorhatte — auch wer den Kranführerschein nicht hat oder
     dessen Schein abgelaufen ist. Das Buchen lehnte ihn dann ab. Jetzt fragt
     das Blatt dieselbe Stelle wie das Buchen (`vorschlag.bediener`), und wer
     nicht geht, **steht mit dem Grund da, statt zu fehlen** — ein leeres Feld
     sähe aus wie „es gibt keinen". Ohne Gerät gibt es keinen Vorschlag; dann
     zählt nur die Belegung. */
  const kandidatJe = useMemo(
    () => new Map((vorschlag?.bediener ?? []).map((k) => [k.id, k])),
    [vorschlag],
  );
  const personenLage = useMemo(
    () => ressourcen
      .filter((r) => r.gruppe === "personal")
      .map((mensch) => {
        const belegt = belegungen.some((b) => b.res === mensch.id && b.fest !== false);
        const k = mensch.res_id ? kandidatJe.get(mensch.res_id) : undefined;
        return { mensch, k, frei: !belegt && (k ? k.frei : true), belegt };
      }),
    [ressourcen, belegungen, kandidatJe],
  );
  /** Wer im Fenster frei ist und das Gerät fahren darf. */
  const freiePersonen = useMemo(
    () => personenLage.filter((x) => x.frei).map((x) => x.mensch),
    [personenLage],
  );
  /** Wer gerade nicht kann — Schein abgelaufen, belegt —, je Mensch mit dem
   *  Satz, warum. Das sind die, nach denen jemand fragt. */
  const gesperrtePersonen = useMemo(
    () => personenLage
      .filter((x) => !x.frei && x.k?.grund !== "qualifikation_fehlt")
      .sort((a, b) => sperrRang(a.k, a.belegt) - sperrRang(b.k, b.belegt))
      .map((x) => ({ mensch: x.mensch, grund: bedienerGrund(t, x.k, x.belegt) })),
    [personenLage, t],
  );
  /** **Wem die Qualifikation ganz fehlt — in einem Satz je Qualifikation.**
   *  Sie stehen da (niemand verschwindet), aber nicht als zehn Zeilen, die
   *  die Wahl unter sich begraben: „Ohne Kranführerschein: B. Schulte, …". */
  const ohneQualifikation = useMemo(() => {
    const je = new Map<string, string[]>();
    personenLage
      .filter((x) => !x.frei && x.k?.grund === "qualifikation_fehlt")
      .forEach((x) => {
        const qual = x.k?.qualifikation ?? "";
        je.set(qual, [...(je.get(qual) ?? []), x.mensch.id]);
      });
    return [...je.entries()].map(([qual, namen]) =>
      t("vermieten.bedienerOhneQual", { qual, namen: namen.join(", ") }));
  }, [personenLage, t]);

  /* **Der Stammfahrer wird vorbelegt, nicht festgelegt** — genau einmal je
     Gerät und Zeitraum. Wer die Vorbelegung abwählt, soll sie nicht beim
     nächsten Tastendruck zurückbekommen (Migration 0070). */
  const fahrerFuer = useRef("");
  useEffect(() => {
    const schluessel = `${p.id}|${stueck?.id ?? ""}|${p.von}|${p.bis}`;
    if (!vorschlag || fahrerFuer.current === schluessel) return;
    fahrerFuer.current = schluessel;
    const fahrer = vorschlag.fahrer;
    if (!fahrer || p.bediener) return;
    const alsRessource = ressourcen.find((r) => r.res_id === fahrer.id);
    if (fahrer.frei && alsRessource) {
      postenAendern(p.id, { bedient: true, bediener: alsRessource.id });
    }
  }, [vorschlag, stueck, p.id, p.von, p.bis, p.bediener, ressourcen]);

  /* Ein Bediener, der im Zeitraum nicht (mehr) frei ist, ist keine Wahl —
     er ist ein Name, der beim Buchen am Ausschluss-Constraint scheitert
     (Regel 8). Also geht er weg, und die Liste zeigt, wer stattdessen kann. */
  useEffect(() => {
    if (!p.bediener || !ressourcen.length) return;
    if (!freiePersonen.some((x) => x.id === p.bediener)) postenAendern(p.id, { bediener: "" });
  }, [p.bediener, p.id, ressourcen, freiePersonen]);

  /* ── Das Zubehör ─────────────────────────────────────────────────
     Der Baustein hält seinen Stand selbst und meldet, welche Stücke
     gebunden werden. Der Posten trägt nur die Kennungen — die Wahl je
     Zuordnung ist Innenleben und rückt bei einem neuen Zeitraum nach.

     **Was der Posten schon trägt, geht als `vorwahl` hinein** (18.09.2026).
     Der Stand beginnt leer, und aus einer leeren Vorwahl belegt
     `wahlVorbelegen` nur **Pflicht** vor: Ein Posten aus einer Wiederholung
     verlor damit sein freiwilliges Zubehör, sobald jemand das Blatt nur
     aufmachte und wieder zumachte. */
  const [zubehoer, setzeZubehoer] = useState<ZubehoerWert>(ZUBEHOER_LEER);
  /* Stabil, weil `ZubehoerWahl` sie in einer Abhängigkeitsliste liest. */
  const vorwahl = useMemo(() => p.zubehoer ?? [], [p.zubehoer]);
  useEffect(() => {
    const stuecke = zubehoer.stuecke;
    const alt = p.zubehoer ?? [];
    if (stuecke.length === alt.length && stuecke.every((x, i) => x === alt[i])) return;
    postenAendern(p.id, { zubehoer: stuecke });
  }, [zubehoer.stuecke, p.zubehoer, p.id]);

  /* ── Bedienung ───────────────────────────────────────────────────
     Was das Gerät verlangt, steht nicht zur Wahl (Migration 0083). */
  const pflicht = p.bedienungspflicht ?? preis?.bedienungspflicht ?? "wahlweise";
  const mussBedient = bedienerPflicht(pflicht);
  const nieBedient = pflicht === "ohne_person";
  const bedienerOffen = mussBedient && !p.bediener;

  /* ── Der Preis von Hand ──────────────────────────────────────────
     **Ein Satz ist ein Satz, keine Summe.** Bei der Tagesstaffel steht der
     Satz je Tag, und die Menge sind die Miettage: Wer 500 tippt, meint 500
     je Tag. Genau diese Verwechslung stand in der Schnellerfassung einmal
     falsch herum und ergab 1.500 auf der Rechnung. */
  const gerechneterSatzJetzt = preis?.einzelpreis ?? 0;
  const { feld: preisFeld, setzeFeld: setzePreisFeld, schreiben: preisSchreiben } = useSatzfeld(p, gerechneterSatzJetzt);

  const zuschlagStand = useMemo(() => {
    const karte: Record<string, Zuschlagsstand> = {};
    p.zuschlaege.forEach((z) => {
      karte[z.code] = {
        ...(z.aus ? { aus: true } : {}),
        ...(z.einzelpreis === undefined ? {} : { einzelpreis: z.einzelpreis }),
      };
    });
    return karte;
  }, [p.zuschlaege]);

  const zuschlagAendern = (code: string, neu: Zuschlagsstand) => {
    postenAendern(p.id, {
      zuschlaege: p.zuschlaege.map((z) => {
        if (z.code !== code) return z;
        /* **Der Rest des Zuschlags bleibt, wie er ist** (19.09.2026): Bis
           hierher wurde die Zeile aus Code, Name, Netto und Menge neu gebaut,
           und dabei fielen `faelltBeiAbholung` und `platzhalter` weg. Neu
           gesetzt wird nur, was die Wahl ändert. Wer von Hand wieder anwählt,
           hat die Abwahl des Schalters damit aufgehoben. */
        const { aus: _aus, einzelpreis: _satz, ausWegenAbholung: _grund, ...rest } = z;
        return {
          ...rest,
          ...(neu.aus ? { aus: true as const } : {}),
          ...(neu.aus && z.ausWegenAbholung ? { ausWegenAbholung: true as const } : {}),
          ...(neu.einzelpreis === undefined ? {} : { einzelpreis: neu.einzelpreis }),
        };
      }),
    });
  };

  /** Die Ausstattung setzen — mit dem Namen, der auf dem Beleg steht. */
  const ausstattungSetzen = (varianteId: string) => {
    if (!typ) return;
    postenAendern(p.id, {
      varianteId,
      variante: typ.varianten.find((v) => v.id === varianteId)?.label ?? "",
      bezeichnung: mitAusstattung(p.typ || typ.label, typ, varianteId),
    });
  };

  /** Der Zeitraum — nur einer, in dem das Gerät frei ist (19.09.2026). */
  const zeitraumSetzen = (neu: Spanne) => {
    if (!neu.von || !neu.bis) return;
    void postenAendernWennFrei(p.id, { von: neu.von, bis: neu.bis });
  };

  /* ══════════════ Die Telefonfassung ══════════════ */
  if (handy) {
    return (
      <Handyblatt
        p={p}
        typ={typ}
        preis={preis}
        frisch={frisch}
        zuschlaege={vorgewaehlt.map((z) => ({ ...z, stand: zuschlagStand[z.code] ?? {} }))}
        zuschlagAendern={zuschlagAendern}
        planDa={plan !== undefined}
        freieStuecke={freieStuecke.map((r) => r.id)}
        reservierteStuecke={reservierteStuecke.map(({ stueck: s, wer }) => ({
          id: s.id, vorgang: wer.vorgang ?? "", kunde: wer.kunde, bis: wer.zusatz ?? "",
        }))}
        personen={freiePersonen.map((m) => ({ id: m.id, kuerzel: m.kuerzel ?? "", qual: (m.qual ?? []).join(` ${t("allgemein.trennpunkt")} `) }))}
        gesperrtePersonen={gesperrtePersonen.map(({ mensch, grund }) => ({ id: mensch.id, kuerzel: mensch.kuerzel ?? "", grund }))}
        ohneQualifikation={ohneQualifikation}
        stammfahrer={vorschlag?.fahrer ?? null}
        mussBedient={mussBedient}
        nieBedient={nieBedient}
        bedienerOffen={bedienerOffen}
        gerechneterSatz={gerechneterSatzJetzt}
        preisFeld={preisFeld}
        setzePreisFeld={setzePreisFeld}
        preisSchreiben={preisSchreiben}
        zubehoer={zubehoer}
        setzeZubehoer={setzeZubehoer}
        vorwahl={vorwahl}
        kunde={kunde}
        ausstattungSetzen={ausstattungSetzen}
        zeitraumSetzen={zeitraumSetzen}
      />
    );
  }

  /* ══════════════ Der Schreibtisch ══════════════ */
  return (
    <div className="pb-blatt">
      {/* ── Wann ───────────────────────────────────────────────────
          **Jeder Posten trägt seinen eigenen Zeitraum** — das ist der
          Kern des Moduls: der Rüttler nächste Woche, der Bagger
          übernächste, in einem Vorgang. */}
      <section className="pb-teil">
        <span className="feld-schild">{t("vermieten.blattWann")}</span>
        <div className="pb-wann">
          <Zeitraum
            id={`pb-zeit-${p.id}`}
            wert={{ von: p.von, bis: p.bis } as Spanne}
            aendern={zeitraumSetzen}
          />
          <span className="pb-miettage">
            {preis ? zeitraumAbBis(t, preis, p.von) : t("allgemein.laedt")}
          </span>
        </div>
      </section>

      {/* ── Wie viele ──────────────────────────────────────────────
          Menge 2 heißt zwei Maschinen, nicht eine Zeile mit einer Zwei:
          Jedes Stück bekommt eine eigene Position und eine eigene
          Belegung (Regel 8). */}
      <section className="pb-teil">
        <span className="feld-schild">
          {t("vermieten.blattMenge")}
          <Erklaerung k="vermieten.blattMengeWas" />
        </span>
        <div className="pb-menge">
          <button
            type="button"
            className="pb-schritt"
            aria-label={t("merkliste.wenigerSchild")}
            disabled={p.menge <= 1}
            onClick={() => postenAendern(p.id, { menge: Math.max(1, p.menge - 1) })}
          >
            −
          </button>
          <span className="pb-stueck">{p.menge}</span>
          <button
            type="button"
            className="pb-schritt"
            aria-label={t("merkliste.mehrSchild")}
            disabled={p.menge >= MENGE_HOECHSTENS}
            onClick={() => { void postenAendernWennFrei(p.id, { menge: Math.min(MENGE_HOECHSTENS, p.menge + 1) }); }}
          >
            +
          </button>
          {freieStuecke.length ? (
            <span className="pb-frei">{t("schnell.geraetFrei", { zahl: freieStuecke.length })}</span>
          ) : (
            <Marke art="stopp" punkt>{t("schnell.geraetKeins")}</Marke>
          )}
        </div>
      </section>

      {/* ── Die Ausstattung ────────────────────────────────────────
          Nur da, wo der Typ welche hat — beim Kran, beim Anhänger. Sie
          steht vor dem Zubehör: Der Arbeitskorb hängt an „mit
          Arbeitskorb", nicht umgekehrt. */}
      {typ && ausstattungVon(typ).gibt ? (
        <section className="pb-teil">
          <label className="feld-schild" htmlFor={`pb-ausstattung-${p.id}`}>
            {t("positionen.ausstattung")}
          </label>
          <AusstattungWahl
            id={`pb-ausstattung-${p.id}`}
            typ={typ}
            wert={p.varianteId}
            aendern={ausstattungSetzen}
          />
        </section>
      ) : null}

      {/* ── Welches Stück ──────────────────────────────────────────
          **Regel 2 bleibt unberührt:** Die Position hängt am Typ, der
          Kunde mietet „Teleskoplader", nicht „MU-01". Nur die Belegung
          bekommt das Individuum — und zwar durch einen Menschen statt
          durch `[0]` (Luca, 16.09.2026). */}
      <section className="pb-teil">
        <span className="feld-schild">
          {t("schnell.geraet")}
          <Erklaerung k="schnell.geraetWas" />
        </span>
        {freieStuecke.length === 0 ? (
          <span className="pb-satz">{t("schnell.geraetKeins")}</span>
        ) : freieStuecke.length === 1 ? (
          <span className="kennung">{freieStuecke[0]?.id}</span>
        ) : (
          <select
            className="fein mono"
            aria-label={t("schnell.geraetWahl", { typ: p.typ })}
            value={p.maschine ?? ""}
            onChange={(e) => postenAendern(p.id, { maschine: e.target.value, reservierungUebergehen: false })}
          >
            {freieStuecke.map((r) => (
              <option key={r.id} value={r.id}>{r.id}</option>
            ))}
          </select>
        )}

        {/* ── Was ein Angebot hält ────────────────────────────────────
            Das Gerät ist da und nicht belegt — es ist versprochen. Wer es
            trotzdem nimmt, sieht wem, bis wann, und bestätigt es hier
            (18.09.2026). Die Bestätigung geht als `reservierung_uebergehen`
            mit; ohne sie weist die API mit `belegung.reserviert` ab.
            Dieselben Zeilen stehen im Abschluss (N3). */}
        <ReservierteStuecke p={p} aendern={postenAendern} />

      </section>

      {/* ── Bedienung ──────────────────────────────────────────────
          Ein Mensch ist eine Ressource (Regel 8) — also wird er
          **gewählt**, nicht vermerkt. Was das Gerät verlangt, steht
          nicht zur Wahl. */}
      {nieBedient ? null : (
        <section className={`pb-teil${bedienerOffen ? " offen" : ""}`}>
          <span className="feld-schild">
            {t("schnell.bedienung")}
            <Erklaerung k="schnell.bedienerOhneSatz" />
            {/* **Wie viele im Zeitraum fahren könnten — am Feld, wie beim
                Gerät** (Systemprüfung A-06). Lisa: *„Es wird nicht angezeigt,
                ob überhaupt ein Fahrer im Zeitraum frei ist."* */}
            {plan ? (
              freiePersonen.length ? (
                <span className="pb-frei">{t("vermieten.bedienerFrei", { zahl: freiePersonen.length })}</span>
              ) : (
                <Marke art="stopp" punkt>{t("vermieten.bedienerKeinerFrei")}</Marke>
              )
            ) : null}
          </span>

          {mussBedient ? (
            <p className="pb-pflicht">
              <Punkt art={p.bediener ? "gut" : "acht"} />
              <span>{t(p.selbstabholung ? "positionen.pflichtGegenAbholungSatz" : "vermieten.bedienerPflicht")}</span>
            </p>
          ) : p.selbstabholung ? (
            /* Holt der Kunde ab, fährt keiner von uns mit — „mit Bediener"
               steht dann nicht zur Wahl (08.10.2026, wie im Angebot). */
            <p className="pb-pflicht">
              <span>{t("merkliste.ohneBedienerAbholung")}</span>
            </p>
          ) : (
            <div className="wahl pb-wahl">
              <button
                type="button"
                aria-pressed={p.bedient}
                onClick={() => postenAendern(p.id, { bedient: true })}
              >
                {t("schnell.mitBediener")}
              </button>
              <button
                type="button"
                aria-pressed={!p.bedient}
                onClick={() => postenAendern(p.id, { bedient: false, bediener: "" })}
              >
                {t("schnell.ohneBediener")}
              </button>
            </div>
          )}

          {/* Der Stammfahrer sagt sich selbst an — und wenn er nicht kann,
              warum. Ihn still wegzulassen hieße, dass niemand erfährt,
              dass es einen gibt (Migration 0070). */}
          {vorschlag?.fahrer ? (
            <div className={`pb-fahrer${vorschlag.fahrer.frei ? " frei" : ""}`}>
              <b>{t("schnell.stammfahrer", { name: vorschlag.fahrer.name })}</b>
              <span>{stammfahrerSatz(t, vorschlag.fahrer)}</span>
            </div>
          ) : null}

          {p.bedient || mussBedient ? (
            freiePersonen.length ? (
              <div className="kunde-liste pb-personen">
                {freiePersonen.map((mensch) => (
                  <button
                    className={`kunde-zeile${p.bediener === mensch.id ? " gewaehlt" : ""}`}
                    type="button"
                    key={mensch.id}
                    aria-pressed={p.bediener === mensch.id}
                    onClick={() => postenAendern(p.id, {
                      bedient: true,
                      bediener: p.bediener === mensch.id ? "" : mensch.id,
                    })}
                  >
                    {/* Bei einem Menschen trägt `id` den Namen und `typ` ist
                        leer — er hat keinen Maschinentyp. */}
                    <span>
                      <b>{mensch.id}</b>{" "}
                      <span className="leise">{(mensch.qual ?? []).join(" · ")}</span>
                    </span>
                    <span className="nr">{mensch.kuerzel}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="pb-satz">{t("schnell.bedienerKeineFrei")}</p>
            )
          ) : null}
          {(p.bedient || mussBedient) && gesperrtePersonen.length ? (
            <div className="kunde-liste pb-personen gesperrt">
              {gesperrtePersonen.map(({ mensch, grund }) => (
                <button className="kunde-zeile" type="button" key={mensch.id} disabled title={grund}>
                  <span>
                    <b>{mensch.id}</b>{" "}
                    <span className="leise">{grund}</span>
                  </span>
                  <span className="nr">{mensch.kuerzel}</span>
                </button>
              ))}
            </div>
          ) : null}
          {p.bedient || mussBedient
            ? ohneQualifikation.map((satz) => <p className="pb-satz" key={satz}>{satz}</p>)
            : null}
        </section>
      )}

      {/* ── Das Zubehör ────────────────────────────────────────────
          Steht nichts Passendes am Typ, zeichnet der Baustein nichts —
          eine leere Zubehörliste bei jedem Radlader wäre Tapete. */}
      <ZubehoerWahl
        typId={p.typId}
        von={p.von}
        bis={p.bis}
        wert={zubehoer}
        aendern={setzeZubehoer}
        vorwahl={vorwahl}
        className="pb-teil"
      />

      {/* **Was davon einen eigenen Tarif hat, kostet etwas — und sagt es
          hier** (Migration 0078, nachgezogen am 18.09.2026). Die Zeile
          entsteht drüben beim Anlegen; bis dahin stand an der Auswahl nur
          „eigener Tarif — eigene Zeile" und nirgends ein Betrag. Sie
          zeichnet nichts, wenn nichts mit eigenem Tarif gebunden ist, und
          die Summe unten rechnet sie über `postenSumme` mit. */}
      <Anbauteile p={p} kunde={kunde} />

      {/* ── Preis und Zuschläge: die Tafel (08.10.2026) ─────────────
          Dieselbe Aufstellung wie an der aufgeklappten Zeile der Merkliste
          und im Abschluss (`Postenaufstellung.tsx`): die Miete mit dem Satz
          von Hand in „Einzel", darunter jeder Zuschlag mit Haken und
          Einzelpreis, dann die Summe. Bis dahin ersetzte hier das Feld den
          Betrag des Zuschlags — man sah nicht mehr, was er kostet. Am
          Telefon wird verhandelt; was zugesagt ist, muss eintragbar sein.
          Eingefroren wird es bei der Annahme wie jeder andere Preis (Regel 4). */}
      <section className="pb-teil">
        <span className="feld-schild">
          {t("merkliste.aufstellung.preisTitel")}
          <Erklaerung k="vermieten.blattPreisWas" />
        </span>
        <Postenaufstellung
          p={p}
          preis={preis}
          frisch={frisch}
          aendern={(teil) => postenAendern(p.id, teil)}
        />
      </section>

      <div className="pb-taten">
        <span className="pb-summe">
          {t("vermieten.blattSumme", { betrag: euro(p.gerechnet ? postenSumme(p) : 0) })}
        </span>
        <Knopf art="haupt" onClick={schliessen}>{t("vermieten.blattFertig")}</Knopf>
      </div>
    </div>
  );
}

/** Der Vorschlag der Disposition, wie `useMaschinenVorschlag` ihn liefert. */
type Fahrervorschlag = {
  id: string; name: string; frei: boolean;
  grund?: string | null | undefined;
  qualifikation?: string | null | undefined;
  belegt_bis?: string | null | undefined;
  gueltig_bis?: string | null | undefined;
};

/** „im Zeitraum frei — vorbelegt, änderbar" oder warum nicht. */
/** `2026-09-22` → `22.09.` — dieselbe Form wie an der Position. */
function tagKurzAus(iso: string | null): string {
  return tagKurz(iso); // deutscher Tag, auch aus einem Zeitpunkt (N8-02)
}

/** Die Reihenfolge der Gesperrten: abgelaufen, belegt, ohne Qualifikation. */
function sperrRang(k: Bedienerkandidat | undefined, belegt: boolean): number {
  if (k?.grund === "qualifikation_abgelaufen") return 0;
  if (k?.grund === "belegt" || belegt) return 1;
  return 2;
}

/**
 * **Warum jemand nicht fahren kann** — in denselben Worten wie an der Position
 * im Vorgang (`positionen.bediener*`, Systemprüfung E-02).
 */
function bedienerGrund(t: Uebersetzer, k: Bedienerkandidat | undefined, belegt: boolean): string {
  if (k?.grund === "qualifikation_abgelaufen") {
    return t("positionen.bedienerAbgelaufen", { qual: k.qualifikation ?? "", am: tagKurzAus(k.gueltigBis) });
  }
  if (k?.grund === "qualifikation_fehlt") return t("positionen.bedienerOhne", { qual: k.qualifikation ?? "" });
  if (k?.grund === "belegt") return t("positionen.bedienerBelegt", { bis: tagKurzAus(k.belegtBis) });
  return belegt ? t("vermieten.bedienerBelegt") : "";
}

function stammfahrerSatz(t: Uebersetzer, fahrer: Fahrervorschlag): string {
  return fahrer.frei
    ? t("schnell.stammfahrerFrei")
    : t(`schnell.fahrerWeg_${fahrer.grund ?? "belegt"}`, {
      qualifikation: fahrer.qualifikation ?? "",
      bis: fahrer.belegt_bis ?? fahrer.gueltig_bis ?? "",
    });
}

/* ══════════════════════════════════════════════════════════════════
   Das Blatt am Telefon (20.09.2026).

   Von oben nach unten, in der Reihenfolge, in der am Telefon gefragt wird:
   ein Satz, wenn etwas fehlt; **wann**; **Stück und Satz**; die
   **Zuschläge** zum Antippen; der **Transport**; die Ausstattung; welches
   **Gerät**; wer **bedient**; die **Anbauteile**. Jede Sache ist ein
   Abschnitt mit einer Karte, jede Zeile 56 px — dieselbe Form wie das
   Angebot (`Positionen.tsx`, `Positionsblatt`), aus denselben Bausteinen
   (`Postendetails.tsx`).

   **Ein Zuschlag ist eine Zeile, die man antippt.** Was abgewählt ist,
   bleibt stehen und sagt, dass es entfällt (und warum, wenn es der
   Schalter „Kunde holt ab" war): Ein Zuschlag, der spurlos verschwindet,
   ist einer, über den drei Wochen später niemand mehr reden kann.

   **Rechnen tut dieses Blatt nicht.** Es zeigt, was `Inhalt` gerechnet und
   der Posten übernommen hat, und ruft dieselben Handgriffe wie der
   Schreibtisch (`postenAendern`, `postenAendernWennFrei`).
   ══════════════════════════════════════════════════════════════════ */
function Handyblatt({
  p, typ, preis, frisch, zuschlaege, zuschlagAendern, planDa, freieStuecke, reservierteStuecke,
  personen, gesperrtePersonen, ohneQualifikation, stammfahrer, mussBedient, nieBedient, bedienerOffen, gerechneterSatz: satzGerechnet,
  preisFeld, setzePreisFeld, preisSchreiben, zubehoer, setzeZubehoer, vorwahl, kunde,
  ausstattungSetzen, zeitraumSetzen,
}: {
  p: Merkposten;
  typ: Maschinentyp | undefined;
  preis: Preisauskunft | undefined;
  frisch: boolean;
  zuschlaege: { code: string; bezeichnung: string; netto: number; menge: number; einzelpreis: number; einheit_key: string; platzhalter?: boolean | undefined; preisIndividuell?: boolean | undefined; stand: Zuschlagsstand }[];
  zuschlagAendern: (code: string, neu: Zuschlagsstand) => void;
  planDa: boolean;
  freieStuecke: string[];
  reservierteStuecke: { id: string; vorgang: string; kunde: string; bis: string }[];
  personen: { id: string; kuerzel: string; qual: string }[];
  /** Wer nicht fahren kann — belegt, ohne oder mit abgelaufener Qualifikation. */
  gesperrtePersonen: { id: string; kuerzel: string; grund: string }[];
  /** Je Qualifikation ein Satz: wem sie ganz fehlt. */
  ohneQualifikation: string[];
  stammfahrer: Fahrervorschlag | null;
  mussBedient: boolean;
  nieBedient: boolean;
  bedienerOffen: boolean;
  gerechneterSatz: number;
  preisFeld: string;
  setzePreisFeld: (text: string) => void;
  preisSchreiben: (text: string) => void;
  zubehoer: ZubehoerWert;
  setzeZubehoer: (neu: ZubehoerWert) => void;
  vorwahl: string[];
  kunde: string;
  ausstattungSetzen: (varianteId: string) => void;
  zeitraumSetzen: (neu: Spanne) => void;
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const transport = abholungMoeglich(p) || (Boolean(p.gespann) && !p.wegen);
  const pruefen = p.zuschlaege.some((z) => !z.aus && z.platzhalter === true);
  /* Preis immer individuell, noch keiner genannt (0227, E1) — so geht der
     Posten nicht als Auftrag hinaus. */
  const ohnePreis = fehlendePreise(p);
  const reserviertGenommen = p.reservierungUebergehen === true && Boolean(p.maschine)
    && reservierteStuecke.some((r) => r.id === p.maschine);
  const mitPerson = p.bedient || mussBedient;

  /* Die Sätze, die keine Zahl sind: Sie stehen oben, damit niemand erst
     rollt, um zu sehen, warum ein Posten so nicht hinausgeht. */
  const saetze = [
    ohnePreis.length ? t("merkliste.preisFehltSatz", { was: ohnePreis.map((z) => z.bezeichnung).join(trenn) }) : "",
    pruefen ? t("positionen.satzBetragPruefen") : "",
    bedienerOffen ? t("vermieten.bedienerPflicht") : "",
    planDa && freieStuecke.length === 0 && !reserviertGenommen ? t("schnell.geraetKeins") : "",
    zubehoer.pflichtFehlt.length
      ? t("zubehoer.pflichtFehltKnopf", { zubehoer: zubehoer.pflichtFehlt.join(trenn) })
      : "",
  ].filter(Boolean);

  const ausstattung = ausstattungVon(typ);
  const einheit = preis?.einheit_key ? t(preis.einheit_key) : "";

  return (
    <div className="posb">
      {saetze.length ? <Kurzfassung>{saetze.join(" ")}</Kurzfassung> : null}

      {/* ── Wann ── Der Kalenderknopf des Hauses; die Miettage und ihre
          Gründe stehen als Satz darunter, wie am Schreibtisch. */}
      <Abschnitt titel={t("vermieten.blattWann")}>
        <Zeitraum
          id={`pb-zeit-${p.id}`}
          wert={{ von: p.von, bis: p.bis } as Spanne}
          schild={t("allgemein.zeitraum")}
          aendern={zeitraumSetzen}
        />
        <Blattsatz>{preis ? zeitraumAbBis(t, preis, p.von) : t("allgemein.laedt")}</Blattsatz>
      </Abschnitt>

      {/* ── Stück und Satz ── Menge 2 heißt zwei Maschinen (Regel 8); der
          Satz gilt je Einheit, nicht als Summe. */}
      <Abschnitt titel={t("positionen.blattRechnung")}>
        <Zeilenblatt>
          <Stueckzeile
            was={t("merkliste.stueck")}
            dazu={planDa
              ? (freieStuecke.length
                ? t("schnell.geraetFrei", { zahl: freieStuecke.length })
                : t("schnell.geraetKeins"))
              : undefined}
            wert={p.menge}
            max={MENGE_HOECHSTENS}
            weniger={() => postenAendern(p.id, { menge: Math.max(1, p.menge - 1) })}
            mehr={() => { void postenAendernWennFrei(p.id, { menge: Math.min(MENGE_HOECHSTENS, p.menge + 1) }); }}
            wenigerSchild={t("merkliste.wenigerSchild")}
            mehrSchild={t("merkliste.mehrSchild")}
          />
          <Zahlzeile
            was={t("positionen.blattSatz")}
            betrag
            schild={t("vermieten.blattPreis")}
            wert={preisFeld}
            platz={betragstext(satzGerechnet)}
            einheit={einheit ? t("positionen.euroJe", { einheit }) : t("rabatt.einheitBetrag")}
            dazu={p.einzelpreis !== undefined
              ? t("schnell.zeileVonHand", { betrag: euro(satzGerechnet) })
              : preis
                ? t("vermieten.preisJe", {
                  menge: preis.menge,
                  // „× 16 Stunden", nicht „× 16 Stunde" (P10-01).
                  einheit: einheitFuer(t, preis.einheit_key, preis.menge),
                  summe: euro(Math.round(preis.einzelpreis * preis.menge * 100) / 100),
                })
                : undefined}
            aendern={setzePreisFeld}
            fertig={preisSchreiben}
          />
          <Wertzeile was={t("positionen.blattMiete")} betrag>
            {p.gerechnet ? euro(Math.round(mietsatz(p) * p.menge * 100) / 100) : t("merkliste.preisOffen")}
          </Wertzeile>
        </Zeilenblatt>
      </Abschnitt>

      {/* ── Die Zuschläge ── */}
      <Abschnitt titel={t("merkliste.detailsZuschlaege")}>
        {preis?.zuschlagsfrei ? (
          <Leersatz>{t("merkliste.ohneZuschlaege")}</Leersatz>
        ) : zuschlaege.length ? (
          <Zeilenblatt>
            {zuschlaege.map((z) => {
              const drin = !z.stand.aus;
              const gemerkt = p.zuschlaege.find((x) => x.code === z.code);
              const betrag = gemerkt ? zuschlagNetto(gemerkt) : (drin ? z.netto : 0);
              const wie = z.einheit_key
                ? t("start.preisZuschlagWie", {
                  menge: z.menge,
                  einheit: einheitFuer(t, z.einheit_key, z.menge),
                  satz: euro(z.stand.einzelpreis ?? z.einzelpreis),
                })
                : "";
              /* Preis immer individuell (0227, E1): ohne genannten Satz kein
                 Betrag, sondern „Preis fehlt" — und darunter das Feld dafür. */
              const fehlt = z.preisIndividuell === true && drin && z.stand.einzelpreis === undefined;
              const unter = drin
                ? fehlt
                  ? t("zuschlaege.preisIndividuell")
                  : [wie, z.platzhalter ? t("merkliste.betragPruefen") : ""].filter(Boolean).join(trenn)
                : t(gemerkt?.ausWegenAbholung ? "merkliste.detailsEntfaelltAbholung" : "merkliste.detailsEntfaellt");
              return (
                <Fragment key={z.code}>
                  <Hakenzeile
                    wort={z.bezeichnung}
                    unter={unter}
                    rechts={fehlt ? t("zuschlaege.preisFehlt") : euro(betrag)}
                    an={drin}
                    aus={!drin}
                    pruefen={drin && (z.platzhalter === true || fehlt)}
                    aendern={(an) => zuschlagAendern(z.code, { ...z.stand, aus: !an })}
                  />
                  {z.preisIndividuell && drin ? (
                    <IndividuellerPreis
                      was={z.bezeichnung}
                      satz={z.stand.einzelpreis}
                      setzen={(satz) => {
                        const { einzelpreis: _weg, ...rest } = z.stand;
                        zuschlagAendern(z.code, satz === undefined ? rest : { ...rest, einzelpreis: satz });
                      }}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </Zeilenblatt>
        ) : (
          <Leersatz>{frisch ? t("merkliste.detailsKeineZuschlaege") : t("allgemein.laedt")}</Leersatz>
        )}
      </Abschnitt>

      {/* ── Transport ── „Kunde holt ab" und „Auflieger mitgeben" — nur, wo
          sie eine Frage sind (`Abholung`, `Gespann`). */}
      {transport ? (
        <Abschnitt titel={t("merkliste.detailsTransport")}>
          <div className="hf-zeilenblatt posb-schalter">
            <Abholung p={p} />
            <Gespann p={p} />
          </div>
        </Abschnitt>
      ) : null}

      {/* ── Die Ausstattung ── nur, wo der Typ welche hat. Wahlzeilen statt
          eines Auswahlfeldes: Am Telefon ist ein `select` ein Fenster, das
          die Maske verdeckt. */}
      {typ && ausstattung.gibt ? (
        <Abschnitt titel={t("positionen.ausstattung")}>
          <Zeilenblatt>
            {ausstattung.grundpreis ? (
              <Wahlzeile
                wort={t("positionen.ausstattungGrund")}
                an={!p.varianteId}
                waehlen={() => ausstattungSetzen("")}
              />
            ) : null}
            {typ.varianten.map((v) => (
              <Wahlzeile
                key={v.id}
                wort={v.label}
                unter={v.qualifikation_label ?? undefined}
                an={p.varianteId === v.id}
                waehlen={() => ausstattungSetzen(v.id)}
              />
            ))}
          </Zeilenblatt>
        </Abschnitt>
      ) : null}

      {/* ── Welches Stück ── Regel 2: Der Kunde mietet den Typ; hinaus geht
          ein Stück, und welches, steht hier — vorbelegt mit dem ersten
          freien, änderbar. Was ein Angebot hält, steht daneben, mit Angebot
          und Frist; wer es nimmt, bestätigt es mit dem Tipp. */}
      <Abschnitt
        titel={t("schnell.geraet")}
        auskunft={planDa && freieStuecke.length ? t("schnell.geraetFrei", { zahl: freieStuecke.length }) : undefined}
      >
        {!planDa ? (
          <Leersatz>{t("allgemein.laedt")}</Leersatz>
        ) : freieStuecke.length === 0 && reservierteStuecke.length === 0 ? (
          <Leersatz>{t("schnell.geraetKeins")}</Leersatz>
        ) : (
          <Zeilenblatt>
            {freieStuecke.map((id) => (
              <Wahlzeile
                key={id}
                wort={<span className="kennung">{id}</span>}
                an={p.maschine === id && !p.reservierungUebergehen}
                waehlen={() => postenAendern(p.id, { maschine: id, reservierungUebergehen: false })}
              />
            ))}
            {reservierteStuecke.map((r) => {
              const gewaehlt = p.maschine === r.id && p.reservierungUebergehen === true;
              return (
                <Wahlzeile
                  key={r.id}
                  wort={<span className="kennung">{r.id}</span>}
                  unter={t("nachschlagen.reserviertWer", { vorgang: r.vorgang, kunde: r.kunde, frist: r.bis })}
                  an={gewaehlt}
                  waehlen={() => postenAendern(p.id, gewaehlt
                    ? { maschine: freieStuecke[0] ?? "", reservierungUebergehen: false }
                    : { maschine: r.id, reservierungUebergehen: true })}
                />
              );
            })}
          </Zeilenblatt>
        )}
        {reserviertGenommen ? <Blattsatz art="acht">{t("schnell.geraetReserviertHinweis")}</Blattsatz> : null}
      </Abschnitt>

      {/* ── Bedienung ── Ein Mensch ist eine Ressource (Regel 8), also wird
          er gewählt. Was das Gerät verlangt, steht nicht zur Wahl — es
          steht als Auskunft am Abschnitt. */}
      {nieBedient ? null : (
        <Abschnitt
          titel={t("schnell.bedienung")}
          auskunft={[
            mussBedient ? t("positionen.bedienerPflicht") : "",
            /* Wie viele im Zeitraum fahren könnten (Systemprüfung A-06). */
            planDa
              ? (personen.length
                ? t("vermieten.bedienerFrei", { zahl: personen.length })
                : t("vermieten.bedienerKeinerFrei"))
              : "",
          ].filter(Boolean).join(trenn) || undefined}
        >
          {!mussBedient || personen.length || gesperrtePersonen.length ? (
            <Zeilenblatt>
              {mussBedient ? null : (
                <Hakenzeile
                  wort={t("schnell.mitBediener")}
                  an={p.bedient}
                  aendern={(an) => postenAendern(p.id, an ? { bedient: true } : { bedient: false, bediener: "" })}
                />
              )}
              {mitPerson ? personen.map((mensch) => (
                <Wahlzeile
                  key={mensch.id}
                  wort={mensch.id}
                  unter={mensch.qual || undefined}
                  rechts={mensch.kuerzel || undefined}
                  an={p.bediener === mensch.id}
                  waehlen={() => postenAendern(p.id, {
                    bedient: true,
                    bediener: p.bediener === mensch.id ? "" : mensch.id,
                  })}
                />
              )) : null}
              {/* Wer nicht geht, steht mit dem Grund da (E-02). */}
              {mitPerson ? gesperrtePersonen.map((mensch) => (
                <Wahlzeile
                  key={mensch.id}
                  wort={mensch.id}
                  unter={mensch.grund}
                  rechts={mensch.kuerzel || undefined}
                  an={false}
                  gesperrt
                  grund={mensch.grund}
                  waehlen={() => undefined}
                />
              )) : null}
            </Zeilenblatt>
          ) : null}
          {mitPerson && planDa && !personen.length ? (
            <Blattsatz art="acht">{t("schnell.bedienerKeineFrei")}</Blattsatz>
          ) : null}
          {mitPerson ? ohneQualifikation.map((satz) => <Blattsatz key={satz}>{satz}</Blattsatz>) : null}
          {mussBedient && !p.bediener ? (
            <Blattsatz art="acht">{t("vermieten.bedienerPflicht")}</Blattsatz>
          ) : null}
          {/* Der Stammfahrer sagt sich selbst an — und wenn er nicht kann,
              warum (Migration 0070). */}
          {stammfahrer ? (
            <Blattsatz>
              {t("schnell.stammfahrer", { name: stammfahrer.name })}{trenn}{stammfahrerSatz(t, stammfahrer)}
            </Blattsatz>
          ) : null}
        </Abschnitt>
      )}

      {/* ── Die Anbauteile ── Der Baustein zeichnet seinen Abschnitt selbst
          und nichts, wo nichts passt. Was davon einen eigenen Tarif hat,
          steht mit seinem Betrag darunter. */}
      <ZubehoerWahl
        typId={p.typId}
        von={p.von}
        bis={p.bis}
        wert={zubehoer}
        aendern={setzeZubehoer}
        vorwahl={vorwahl}
        handy
      />
      <Anbauteile p={p} kunde={kunde} handy />
    </div>
  );
}

/**
 * **Der Preis eines Zuschlags, der immer individuell vergeben wird** (Migration
 * 0227, Entscheidung E1 — die Anlieferung), am Telefon.
 *
 * Am Schreibtisch steht das Feld in der Zuschlagsanzeige; hier unter der
 * Hakenzeile, mit Ziffernblock und zwei Nachkommastellen (E11). Geschrieben
 * wird beim Verlassen; leer heißt: noch kein Preis.
 */
function IndividuellerPreis({ was, satz, setzen }: {
  was: string;
  satz: number | undefined;
  setzen: (satz: number | undefined) => void;
}) {
  const t = useT();
  const [text, setzeText] = useState(satz === undefined ? "" : betragstext(satz));
  return (
    <Zahlzeile
      was={t("zuschlaege.preisFuer", { was })}
      betrag
      wert={text}
      platz={t("zuschlaege.preisEingeben")}
      einheit={t("rabatt.einheitBetrag")}
      aendern={setzeText}
      fertig={(w) => {
        const zahl = zahlLesen(w);
        // Zwei Stellen nach dem Verlassen, wie in jedem Betragsfeld (N6-01).
        if (zahl !== null && zahl > 0) setzeText(betragVerlassen(w, satz).text);
        setzen(zahl === null || zahl <= 0 ? undefined : zahl);
      }}
    />
  );
}
